// SPDX-License-Identifier: AGPL-3.0-or-later

import {SettingsSection} from '@app/features/app/components/dialogs/shared/SettingsSection';
import {SettingsTabContainer, SettingsTabContent} from '@app/features/app/components/dialogs/shared/SettingsTabLayout';
import {StatusSlate} from '@app/features/app/components/dialogs/shared/StatusSlate';
import type {DeveloperApplication} from '@app/features/devtools/models/DeveloperApplication';
import {TRY_AGAIN_DESCRIPTOR} from '@app/features/i18n/utils/CommonMessageDescriptors';
import {Button} from '@app/features/ui/button/Button';
import * as ModalCommands from '@app/features/ui/commands/ModalCommands';
import {modal} from '@app/features/ui/commands/ModalCommands';
import {Spinner} from '@app/features/ui/components/Spinner';
import {WarningCircleIcon} from '@app/features/ui/icons/Icons';
import {Tooltip} from '@app/features/ui/tooltip/Tooltip';
import {ApplicationCreateModal} from '@app/features/user/components/modals/tabs/applications_tab/ApplicationCreateModal';
import {ApplicationDetail} from '@app/features/user/components/modals/tabs/applications_tab/ApplicationDetail';
import {ApplicationsList} from '@app/features/user/components/modals/tabs/applications_tab/ApplicationsList';
import styles from '@app/features/user/components/modals/tabs/applications_tab/ApplicationsTab.module.css';
import ApplicationsTabState from '@app/features/user/components/modals/tabs/applications_tab/ApplicationsTabState';
import {useSettingsContentKey} from '@app/features/user/hooks/useSettingsContentKey';
import {useUnsavedChangesFlash} from '@app/features/user/hooks/useUnsavedChangesFlash';
import Users from '@app/features/user/state/Users';
import {msg} from '@lingui/core/macro';
import {Trans, useLingui} from '@lingui/react/macro';
import {observer} from 'mobx-react-lite';
import type React from 'react';
import {useCallback, useEffect, useLayoutEffect} from 'react';

const CLAIM_YOUR_ACCOUNT_TO_CREATE_APPLICATIONS_DESCRIPTOR = msg({
	message: 'Claim your account to create applications.',
	comment: 'Description text in the index.',
});
const ApplicationsTab: React.FC = observer(() => {
	const {i18n} = useLingui();
	const {checkUnsavedChanges} = useUnsavedChangesFlash('applications');
	const {setContentKey} = useSettingsContentKey();
	const store = ApplicationsTabState;
	const isUnclaimed = !(Users.currentUser?.isClaimed() ?? false);
	useLayoutEffect(() => {
		setContentKey(store.contentKey);
	}, [store.contentKey, setContentKey]);
	useEffect(() => {
		void store.fetchApplications({showLoading: store.applications.length === 0});
	}, [store]);
	const handleSelectApplication = useCallback(
		(appId: string) => {
			if (checkUnsavedChanges()) return;
			void store.navigateToDetail(appId);
		},
		[store, checkUnsavedChanges],
	);
	const openCreateModal = useCallback(() => {
		ModalCommands.push(
			modal(() => (
				<ApplicationCreateModal
					onCreated={async (app: DeveloperApplication) => {
						await store.navigateToDetail(app.id, app);
						void store.fetchApplications({showLoading: false});
					}}
					data-flx="user.applications-tab.open-create-modal.application-create-modal"
				/>
			)),
		);
	}, [store]);
	const handleBackToList = useCallback(() => {
		if (checkUnsavedChanges()) return;
		void store.navigateToList();
	}, [store, checkUnsavedChanges]);
	if (store.navigationState === 'LOADING_LIST' || (store.isLoading && store.isListView)) {
		return (
			<SettingsTabContainer data-flx="user.applications-tab.settings-tab-container">
				<SettingsTabContent data-flx="user.applications-tab.settings-tab-content">
					<div className={styles.spinnerContainer} data-flx="user.applications-tab.spinner-container">
						<Spinner data-flx="user.applications-tab.spinner" />
					</div>
				</SettingsTabContent>
			</SettingsTabContainer>
		);
	}
	if (store.navigationState === 'ERROR' && store.isListView) {
		return (
			<SettingsTabContainer data-flx="user.applications-tab.settings-tab-container--2">
				<SettingsTabContent data-flx="user.applications-tab.settings-tab-content--2">
					<SettingsSection
						id="applications-list"
						title={<Trans>Applications</Trans>}
						data-flx="user.applications-tab.settings-tab-section"
					>
						<StatusSlate
							Icon={WarningCircleIcon}
							title={<Trans>Unable to load applications</Trans>}
							description={<Trans>Check your connection and try again.</Trans>}
							actions={[
								{
									text: i18n._(TRY_AGAIN_DESCRIPTOR),
									onClick: () => store.fetchApplications({showLoading: true}),
								},
							]}
							data-flx="user.applications-tab.status-slate"
						/>
					</SettingsSection>
				</SettingsTabContent>
			</SettingsTabContainer>
		);
	}
	if (store.isDetailView && store.selectedAppId) {
		return (
			<SettingsTabContainer data-flx="user.applications-tab.settings-tab-container--3">
				<SettingsTabContent data-flx="user.applications-tab.settings-tab-content--3">
					<ApplicationDetail
						applicationId={store.selectedAppId}
						onBack={handleBackToList}
						initialApplication={store.selectedApplication}
						data-flx="user.applications-tab.application-detail"
					/>
				</SettingsTabContent>
			</SettingsTabContainer>
		);
	}
	return (
		<SettingsTabContainer data-flx="user.applications-tab.settings-tab-container--4">
			<SettingsTabContent data-flx="user.applications-tab.settings-tab-content--4">
				<SettingsSection
					id="applications-list"
					title={<Trans>Applications</Trans>}
					data-flx="user.applications-tab.settings-tab-section--2"
				>
					<div className={styles.buttonContainer} data-flx="user.applications-tab.button-container">
						{isUnclaimed ? (
							<Tooltip
								text={i18n._(CLAIM_YOUR_ACCOUNT_TO_CREATE_APPLICATIONS_DESCRIPTOR)}
								data-flx="user.applications-tab.tooltip"
							>
								<div data-flx="user.applications-tab.div">
									<Button
										variant="primary"
										fitContent
										onClick={openCreateModal}
										disabled
										data-flx="user.applications-tab.button.open-create-modal"
									>
										<Trans>Create application</Trans>
									</Button>
								</div>
							</Tooltip>
						) : (
							<Button
								variant="primary"
								fitContent
								onClick={openCreateModal}
								data-flx="user.applications-tab.button.open-create-modal--2"
							>
								<Trans>Create application</Trans>
							</Button>
						)}
					</div>
					<ApplicationsList
						applications={store.applications}
						onSelectApplication={handleSelectApplication}
						data-flx="user.applications-tab.applications-list"
					/>
				</SettingsSection>
			</SettingsTabContent>
		</SettingsTabContainer>
	);
});

export default ApplicationsTab;
